/**
 * Page 4: the AI news draft.
 *
 * WIRED on 1 November 2026, after the open question it was held for was finally
 * answered by reading a real draft off production rather than reasoning about
 * one.
 *
 * ⚠️ THE DESIGN'S CITATION FORMAT DOES NOT MATCH THE GENERATOR, and this page
 * follows the generator. The handoff draws `[1]` `[2]` markers in the prose and
 * a numbered "Source references" sidebar. The `DEFAULT_BRIEF_2_CITATIONS`
 * prompt produces neither. What it actually returns, measured on a real
 * campaign draft, is:
 *
 *   - `result.header` and `result.subheader`, plain strings;
 *   - `result.brief`, HTML paragraphs with the attribution INLINE and named,
 *     `... according to <a href="…">The Independent</a>`;
 *   - a closing paragraph listing the publishers used, as links;
 *   - zero `[n]` markers anywhere.
 *
 * Rendering numbered markers would mean changing a prompt the authenticated
 * product shares, to make the output worse: a named publisher inline is more
 * useful to a journalist than a number they have to look up. So the page shows
 * the real thing and the deviation is recorded for Adi rather than hidden.
 *
 * The body is model-written HTML and is rendered as HTML, which is the same
 * decision the authenticated editor makes about the same field. It is not
 * visitor input and never round-trips through a user.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import TrialShell from './TrialShell';
import TrialExpired from './TrialExpired';
import trialApi from './trialApi';
import { useFocusHeading } from './useTrialPage';
import { EVENTS, track } from '../../analytics/campaignAnalytics';

/** Generation takes tens of seconds, so this polls rather than blocking. */
const POLL_MS = 4000;
/** About four minutes. Past that something is wrong and saying so beats spinning. */
const MAX_POLLS = 60;

export default function DraftPage() {
  const { token, storyId } = useParams();
  const heading = useFocusHeading(true);

  const [state, setState] = useState('starting');
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const polls = useRef(0);
  const timer = useRef(null);
  const alive = useRef(true);

  useEffect(() => () => {
    alive.current = false;
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const poll = useCallback(async (reportId) => {
    if (!alive.current) return;
    if (polls.current >= MAX_POLLS) {
      setState('slow');
      return;
    }
    polls.current += 1;
    try {
      const res = await trialApi.getDraft(token, reportId);
      if (!alive.current) return;
      if (res.status === 'ready') {
        setDraft(res);
        setState('ready');
        track(EVENTS.AI_DRAFT_GENERATED, { story_id: storyId });
        return;
      }
      if (res.status === 'failed') {
        setState('failed');
        return;
      }
      timer.current = setTimeout(() => poll(reportId), POLL_MS);
    } catch (err) {
      if (!alive.current) return;
      setError(err);
      setState('error');
    }
  }, [token, storyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await trialApi.requestDraft(token, storyId);
        if (cancelled || !alive.current) return;
        track(EVENTS.AI_DRAFT_REQUESTED, { story_id: storyId, reused: res.alreadyRequested });
        if (res.status === 'ready') {
          const full = await trialApi.getDraft(token, res.reportId);
          if (!cancelled && alive.current) { setDraft(full); setState('ready'); }
          return;
        }
        setState('working');
        poll(res.reportId);
      } catch (err) {
        if (cancelled || !alive.current) return;
        setError(err);
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, storyId, poll]);

  if (error?.code === 'TRIAL_LINK_EXPIRED' || error?.code === 'TRIAL_LINK_INVALID') {
    return <TrialExpired code={error.code} />;
  }

  const back = `/trial/feed/${token}/story/${storyId}`;

  return (
    <TrialShell
      token={token}
      active="feed"
      onContactClick={() => track(EVENTS.CONTACT_CLICKED)}
      onSiteClick={() => track(EVENTS.SITE_LINK_CLICKED)}
    >
      <main className="tr-main">
        <div className="tr-wrap tr-wrap--story">
          <Link className="tr-back" to={back}>
            <span aria-hidden="true">&#8592;</span> Back to story &amp; sources
          </Link>

          <h1 className="tr-h1" tabIndex={-1} ref={heading} style={{ marginTop: 16 }}>
            Your AI news draft
          </h1>

          {(state === 'starting' || state === 'working') && (
            <section className="tr-panel" style={{ marginTop: 24 }} role="status">
              <p className="tr-body">
                Writing your draft from the sources on the previous page. This usually takes
                under a minute.
              </p>
              <p className="tr-meta" style={{ marginTop: 8 }}>
                You can leave this page open. It will update by itself.
              </p>
            </section>
          )}

          {state === 'slow' && (
            <section className="tr-panel" style={{ marginTop: 24 }} role="status">
              <h2 className="tr-h2">This is taking longer than usual</h2>
              <p className="tr-body" style={{ marginTop: 8 }}>
                The draft is still being written. Open this page again in a few minutes and it
                will be here.
              </p>
              <p className="tr-meta" style={{ marginTop: 8 }}>
                Nothing has been lost, and this has not used another of your drafts.
              </p>
            </section>
          )}

          {state === 'failed' && (
            <section className="tr-panel" style={{ marginTop: 24 }} role="alert">
              <h2 className="tr-h2">We could not write this one</h2>
              <p className="tr-body" style={{ marginTop: 8 }}>
                {/* Q14: a failed generation does not count against the allowance,
                    and saying so stops it reading as a penalty. */}
                A failed draft does not use one of your trial drafts. The story and all of its
                sources are still available.
              </p>
              <Link className="tr-btn tr-btn--secondary" to={back} style={{ marginTop: 16 }}>
                Back to story &amp; sources
              </Link>
            </section>
          )}

          {state === 'error' && (
            <section className="tr-panel" style={{ marginTop: 24 }} role="alert">
              <h2 className="tr-h2">
                {error?.code === 'CAMPAIGN_ALLOWANCE_EXHAUSTED'
                  ? 'You’ve used the AI drafts included in your trial'
                  : 'We could not start that draft'}
              </h2>
              <p className="tr-body" style={{ marginTop: 8 }}>
                {error?.code === 'CAMPAIGN_ALLOWANCE_EXHAUSTED'
                  ? 'Stories and sources stay fully available.'
                  : (error?.message || 'Please try again from the story page.')}
              </p>
              <Link className="tr-btn tr-btn--secondary" to={back} style={{ marginTop: 16 }}>
                Back to story &amp; sources
              </Link>
            </section>
          )}

          {state === 'ready' && draft?.result && <Draft result={draft.result} />}
        </div>
      </main>
    </TrialShell>
  );
}

/**
 * The draft itself.
 *
 * No numbered sidebar, because there are no numbers to key it to. The
 * generator attributes inline and names the publisher, which is what the
 * closing "Sources" paragraph of its own output already lists.
 */
/** The number of articles behind a draft, whether the API sent a list or a count. */
function articleCount(sourcesUsed) {
  if (Array.isArray(sourcesUsed)) return sourcesUsed.length;
  return Number(sourcesUsed) || 0;
}

function Draft({ result }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      const plain = [result.header, result.subheader, stripTags(result.brief)]
        .filter(Boolean).join('\n\n');
      await navigator.clipboard.writeText(plain);
      setCopied(true);
      track(EVENTS.AI_DRAFT_COPIED);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // A clipboard the browser refuses is not worth an error dialog; the text
      // is on screen and selectable.
      setCopied(false);
    }
  };

  return (
    <>
      <article className="tr-panel tr-draft" style={{ marginTop: 24 }}>
        {result.header && <h2 className="tr-h2 tr-draft-head">{result.header}</h2>}
        {result.subheader && <p className="tr-lede" style={{ marginTop: 8 }}>{result.subheader}</p>}
        {/*
          * Model-written HTML, rendered as HTML. Same decision the authenticated
          * editor makes about this same field: it is generated prose with
          * publisher links, never visitor input, and it does not round-trip
          * through a user on the way here.
          */}
        <div
          className="tr-draft-body"
          style={{ marginTop: 16 }}
          dangerouslySetInnerHTML={{ __html: result.brief || '' }}
        />
      </article>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
        <button type="button" className="tr-btn tr-btn--primary" onClick={copy}>
          {copied ? 'Copied' : 'Copy draft'}
        </button>
        {typeof result.distinctPublishersUsed === 'number' && (
          <span className="tr-meta">
            {/*
              * `sourcesUsed` is an ARRAY of article objects (`app.js` builds it
              * with `articles.map(a => ({...}))`), not a count. Interpolating it
              * directly rendered "Written from [object Object],[object Object]
              * articles" on production. Counted here rather than renamed,
              * because the main brief UI consumes the same field as a list.
              */}
            {`Written from ${articleCount(result.sourcesUsed)} articles across ${result.distinctPublishersUsed} publishers`}
          </span>
        )}
      </div>

      <p className="tr-meta" style={{ marginTop: 16, fontStyle: 'italic' }}>
        AI-generated content should be reviewed and fact-checked before publication.
      </p>
    </>
  );
}

/** Clipboard text, not display. Keeps the copy readable without the markup. */
function stripTags(html) {
  return String(html || '')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
